// frontend/src/components/RequireAuth.tsx
import { useEffect, useMemo, useState } from "react";
import { useLocation, Navigate } from "react-router-dom";
import { apiAuthMe } from "../lib/api";

/**
 * RequireAuth
 * - Normal: redirect unauthenticated users to /login?redirect=...
 * - Exception: while on ANY trust wish route, never auto-redirect away.
 *   Prevents race-condition bounces during OTP/verify flow.
 *   (basename="/app" → location.pathname has no "/app" prefix)
 */
export default function RequireAuth({ children }: { children: JSX.Element }) {
  const loc = useLocation();
  const [loading, setLoading] = useState(true);
  const [authed, setAuthed] = useState<boolean | null>(null);

  // Guard all wish routes (basename-aware)
  const onWishFlow = useMemo(() => {
    const p = loc.pathname;
    return (
      /^\/trusts\/wishes(\/|$)/.test(p) || // /trusts/wishes/:id, :id/verify
      /^\/trusts\/\d+\/wishes\/create$/.test(p)
    );
  }, [loc.pathname]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await apiAuthMe();
        if (!alive) return;
        setAuthed(!!res?.user);
      } catch {
        if (!alive) return;
        setAuthed(false);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [loc.pathname]);

  if (loading) {
    return <div className="min-h-screen grid place-items-center">Loading…</div>;
  }

  if (!authed) {
    // 🔒 Wish flow exception: do NOT redirect away (render child route)
    if (onWishFlow) {
      return children;
    }
    const redirect = encodeURIComponent(loc.pathname + loc.search);
    return <Navigate to={`/login?redirect=${redirect}`} replace />;
  }

  return children;
}
